/**
 * AuthProvider — wraps the app with Supabase Google OAuth authentication.
 *
 * Provides auth context to the entire application, handles login/logout,
 * and enforces the user whitelist. Unauthorised accounts are blocked.
 *
 * Multi-role users (adviser email) can choose to operate as either
 * Adviser or Assistant. The chosen role is stored in sessionStorage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../api/supabaseClient';
import { isUserAuthorised, getUserRoles } from './authConfig';
import { getIntroVideoLastPlayedDate } from '../api/userProfilesApi';
import { getTodaySASTDateString } from '../utils/dateHelpers';

const AuthContext = createContext(null);
const ROLE_STORAGE_KEY = 'inbox_chosen_role';

/**
 * Hook to access auth state throughout the app.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [needsRoleChoice, setNeedsRoleChoice] = useState(false);
  const [needsIntroVideo, setNeedsIntroVideo] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  // True while selectRole is awaiting the intro-video SAST-date gate read.
  // ProtectedRoute treats this like isLoading so the dashboard cannot flash
  // between the role click and the gate resolving.
  const [isCheckingIntroGate, setIsCheckingIntroGate] = useState(false);

  // Tracks whether the user has actively clicked a role card in this page
  // lifecycle. Prevents token-refresh events from kicking the user back to the
  // role chooser mid-session, while still requiring a card click on every
  // fresh page load or login.
  const activeRoleRef = useRef(null);

  /**
   * Silent intro-video gate for role-restore paths (token refreshes in a
   * long-lived tab). Compares the user's stored SAST date with today and
   * raises needsIntroVideo when they differ (or no row exists / the read
   * fails — fail open, per the gate's design). Unlike the selectRole gate
   * it never touches isCheckingIntroGate: a background re-check must not
   * flash a loading screen over a working dashboard. Same-day checks are
   * visually silent.
   */
  const runIntroGateSilently = useCallback(async (userId) => {
    if (!userId) return;
    const today = getTodaySASTDateString();
    const last = await getIntroVideoLastPlayedDate(userId);
    if (last !== today) {
      setNeedsIntroVideo(true);
    }
  }, []);

  /**
   * Process a Supabase session and set user state.
   *
   * The intro video gate runs on EVERY role resolution — a role-card click
   * (selectRole) or the in-memory role restore below — and is guarded
   * solely by the per-user SAST date in user_profiles. There is no
   * fresh-login flag; the DB date comparison is the only once-per-day
   * check, so re-running the gate on any path is always harmless.
   */
  const processSession = useCallback((session) => {
    if (session?.user) {
      const email = session.user.email || '';
      const name =
        session.user.user_metadata?.full_name ||
        session.user.user_metadata?.name ||
        email;
      const authorised = isUserAuthorised(email);

      setUser({ name, email, id: session.user.id });
      setIsAuthenticated(true);
      setIsAuthorised(authorised);

      // Determine which roles this user can access
      const roles = getUserRoles(email);
      setAvailableRoles(roles);

      if (activeRoleRef.current && roles.includes(activeRoleRef.current)) {
        // Role was actively selected via card click in this page session —
        // keep it (handles token-refresh events without disrupting the user).
        // The gate still re-runs silently: a tab left open across the SAST
        // midnight boundary must offer the new day's video instead of
        // riding the restored role past the gate.
        setRole(activeRoleRef.current);
        setNeedsRoleChoice(false);
        runIntroGateSilently(session.user.id);
      } else {
        // No active selection — always show the role chooser, for every
        // user, regardless of how many roles they hold (single-role users
        // included). The user must click their role card to enter the app.
        activeRoleRef.current = null;
        setRole(null);
        setNeedsRoleChoice(roles.length > 0);
      }
      // Note: the click-path gate runs in selectRole() after the user picks a role.
    } else {
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
      setIsAuthorised(false);
      setNeedsRoleChoice(false);
    }
    setIsLoading(false);
  }, [runIntroGateSilently]);

  /**
   * On mount, check for an existing Supabase session and listen for changes.
   */
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      processSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        processSession(session);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [processSession]);

  /**
   * Sign in with Google via Supabase OAuth.
   */
  const login = useCallback(async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  }, []);

  /**
   * Sign out and clear all Inbox session and local storage.
   */
  const logout = useCallback(async () => {
    try {
      // Clear active role ref and all sessionStorage keys
      activeRoleRef.current = null;
      sessionStorage.removeItem(ROLE_STORAGE_KEY);

      // Clear all Inbox localStorage keys
      localStorage.removeItem('inbox_sla_warned');
      localStorage.removeItem('inbox_settings');
      localStorage.removeItem('inbox_reports_sent');
      localStorage.removeItem('inbox_processed_events');
      if (user?.email) {
        localStorage.removeItem(`inbox_achievements_${user.email}`);
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [user]);

  /**
   * Select a role (chooser card click — any user, any number of roles).
   *
   * The intro-video gate runs UNCONDITIONALLY on every invocation: compare
   * today's SAST date with user_profiles.intro_video_last_played_date and
   * raise the overlay when they differ (or no row exists). The DB date is
   * the only once-per-day guard — there is no fresh-login detection, so a
   * second login the same day simply resolves to "seen today" and lands on
   * the dashboard. A failed read fails open — better to show the video
   * than to silently swallow it.
   *
   * To avoid the dashboard flashing between the click and the async gate
   * resolving, we set `isCheckingIntroGate = true` first (ProtectedRoute
   * renders LoadingScreen while it's true) and only commit `role` and
   * `needsIntroVideo` together once the DB read returns.
   */
  const selectRole = useCallback(async (chosenRole) => {
    activeRoleRef.current = chosenRole;
    sessionStorage.setItem(ROLE_STORAGE_KEY, chosenRole);

    setNeedsRoleChoice(false);
    setIsCheckingIntroGate(true);

    const today = getTodaySASTDateString();
    const last = await getIntroVideoLastPlayedDate(user?.id);

    if (last !== today) {
      setNeedsIntroVideo(true);
    }
    setRole(chosenRole);
    setIsCheckingIntroGate(false);
  }, [user?.id]);

  /**
   * Switch role — clears saved role and shows the role chooser again.
   */
  const switchRole = useCallback(() => {
    activeRoleRef.current = null;
    sessionStorage.removeItem(ROLE_STORAGE_KEY);
    setRole(null);
    setNeedsRoleChoice(true);
  }, []);

  /**
   * Clear intro video state after video has played.
   */
  const clearIntroVideo = useCallback(() => {
    setNeedsIntroVideo(false);
  }, []);

  const value = {
    user,
    role,
    availableRoles,
    isAuthenticated,
    isAuthorised,
    isLoading,
    isCheckingIntroGate,
    needsRoleChoice,
    needsIntroVideo,
    login,
    logout,
    selectRole,
    switchRole,
    clearIntroVideo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
